/**
 * Pohyb figúrok. Oproti holému grafu mapy rieši obsadenosť priestorov:
 *
 *   • v jednom priestore smie stáť najviac jedna figúrka,
 *   • cez vlastné figúrky sa prejsť dá, cez súperove nie,
 *   • pohyb musí skončiť v prázdnom priestore.
 *
 * (Core Rules, „Step 2: Move your fighters" — viď docs/PRAVIDLA.md)
 */

import { neighbors } from './map.js';
import { areEnemies } from './players.js';
import type { Fighter, FighterId, GameMap, GameState, SpaceId } from './types.js';

/**
 * Priestory obsadené súperovými figúrkami — cez tie sa prejsť nedá.
 * Figúrky spoluhráča sú priateľské, takže cez ne prejsť smieš (v 2v2).
 */
export function blockedFor(state: GameState, fighter: Fighter, through: Through = false): Set<SpaceId> {
  const blocked = new Set<SpaceId>();
  if (through === true) return blocked;
  for (const other of state.fighters) {
    // Lord Kiso's Final Stand: „may move through sidekicks" — hrdinovia blokujú ďalej.
    if (through === 'sidekicks' && other.kind === 'sidekick') continue;
    if (areEnemies(state, other.owner, fighter.owner)) blocked.add(other.spaceId);
  }
  return blocked;
}

/** Cez koho smie presun prejsť: nikoho, všetkých súperov, alebo len súperových sidekickov. */
export type Through = boolean | 'sidekicks';

/**
 * Figúrka tento ťah neopustí svoj priestor — Terrifying Roar (všetky) alebo
 * pripnutie jednej (Nerve Cluster Strike, Refuse to Retreat).
 */
export function isPinned(state: GameState, fighterId: FighterId): boolean {
  return Boolean(state.turn.rooted || state.turn.pinned?.includes(fighterId));
}

/**
 * Figúrky, ktoré sa smú presúvať medzi priestormi s vlastnou hmlou, akoby
 * susedili (schopnosť Invisible Mana). Je to vlastnosť postavy, nie karty —
 * preto zoznam ID tu, nie v registri efektov (ten importuje tento modul).
 */
export const FOG_WALKERS: ReadonlySet<string> = new Set(['invisible-man']);

/**
 * Veľké figúrky (Jurassic Park: T. Rex). Útočia do 2 priestorov aj ponad
 * figúrky a nesmú používať tajné chodby. (Fyzický podstavec, ktorý niekedy
 * zaberie aj druhý priestor, engine nemodeluje — graf mapy tvar nepozná.)
 */
export const LARGE_FIGHTERS: ReadonlySet<string> = new Set(['t-rex']);

/**
 * Figúrky, ktoré útočia aj na viac priestorov (ponad figúrky, bez ohľadu na zóny):
 * veľké figúrky a Ms. Marvel (STRETCHY) na 2, Bullseye na 5.
 */
export const ATTACK_REACH: Readonly<Record<string, number>> = { 't-rex': 2, 'ms-marvel': 2, bullseye: 5 };

/** Susedia cez tajnú chodbu (Baskerville Manor) — len pre pohyb. */
function passageNeighbors(map: GameMap, spaceId: SpaceId): SpaceId[] {
  const out: SpaceId[] = [];
  for (const group of map.passages ?? []) {
    if (group.includes(spaceId)) out.push(...group.filter((id) => id !== spaceId));
  }
  return out;
}

/** Kam sa figúrka dostane jedným krokom — čiary mapy, tajné chodby a prípadne skok cez hmlu. */
export function stepNeighbors(map: GameMap, state: GameState, fighter: Fighter, spaceId: SpaceId): SpaceId[] {
  const lines = neighbors(map, spaceId);
  // Veľké figúrky tajnými chodbami neprejdú (Rules Hub — map rulings).
  const secret = LARGE_FIGHTERS.has(fighter.defId)
    ? []
    : passageNeighbors(map, spaceId).filter((id) => !lines.includes(id));
  const base = secret.length > 0 ? [...lines, ...secret] : lines;
  if (!FOG_WALKERS.has(fighter.defId)) return base;
  const fog = (state.tokens ?? []).filter((t) => t.kind === 'fog' && t.owner === fighter.owner);
  if (!fog.some((t) => t.spaceId === spaceId)) return base;
  const jumps = fog.map((t) => t.spaceId).filter((id) => id !== spaceId && !base.includes(id));
  return [...base, ...new Set(jumps)];
}

export function isOccupied(state: GameState, spaceId: SpaceId, ignore?: string): boolean {
  return state.fighters.some((f) => f.spaceId === spaceId && f.id !== ignore);
}

/**
 * Všetky legálne ciele pohybu → cesta k nim (vrátane východiskového priestoru).
 * `extraMove` je bonus z boostu.
 */
export function movePaths(
  map: GameMap,
  state: GameState,
  fighter: Fighter,
  extraMove = 0,
): Map<SpaceId, SpaceId[]> {
  return pathsWithin(map, state, fighter, Math.max(0, fighter.move + extraMove));
}

/**
 * Ciele dosiahnuteľné na daný počet krokov. Efekty kariet hovoria „presuň sa
 * až o N priestorov" (nie o `move` figúrky) a niektoré dovoľujú prejsť aj cez
 * súperove figúrky — skončiť sa však vždy musí na prázdnom priestore.
 */
export function pathsWithin(
  map: GameMap,
  state: GameState,
  fighter: Fighter,
  maxSteps: number,
  through: Through = false,
): Map<SpaceId, SpaceId[]> {
  const blocked = blockedFor(state, fighter, through);
  // Terrifying Roar a pripnutie: figúrka tento ťah neopustí svoj priestor.
  if (isPinned(state, fighter.id)) return new Map([[fighter.spaceId, [fighter.spaceId]]]);

  // BFS cez priestory, ktoré nie sú obsadené súperom.
  const paths = new Map<SpaceId, SpaceId[]>([[fighter.spaceId, [fighter.spaceId]]]);
  let frontier: SpaceId[] = [fighter.spaceId];

  for (let step = 1; step <= maxSteps && frontier.length > 0; step++) {
    const next: SpaceId[] = [];
    for (const current of frontier) {
      const prefix = paths.get(current)!;
      for (const neighbor of stepNeighbors(map, state, fighter, current)) {
        if (paths.has(neighbor) || blocked.has(neighbor)) continue;
        paths.set(neighbor, [...prefix, neighbor]);
        next.push(neighbor);
      }
    }
    frontier = next;
  }

  // Skončiť sa smie len v prázdnom priestore (vlastný sa počíta ako prázdny — 0 krokov).
  for (const spaceId of [...paths.keys()]) {
    if (spaceId !== fighter.spaceId && isOccupied(state, spaceId, fighter.id)) {
      paths.delete(spaceId);
    }
  }
  return paths;
}

/** Overí konkrétnu cestu — server neverí ceste od klienta. */
export function isLegalPath(
  map: GameMap,
  state: GameState,
  fighter: Fighter,
  path: readonly SpaceId[],
  extraMove = 0,
): boolean {
  return isLegalPathWithin(map, state, fighter, path, fighter.move + extraMove);
}

export function isLegalPathWithin(
  map: GameMap,
  state: GameState,
  fighter: Fighter,
  path: readonly SpaceId[],
  maxSteps: number,
  through: Through = false,
): boolean {
  if (path.length === 0 || path[0] !== fighter.spaceId) return false;
  if (path.length - 1 > maxSteps) return false;
  if (path.length > 1 && isPinned(state, fighter.id)) return false;

  const blocked = blockedFor(state, fighter, through);
  for (let i = 1; i < path.length; i++) {
    const prev = path[i - 1] as SpaceId;
    const current = path[i] as SpaceId;
    if (!stepNeighbors(map, state, fighter, prev).includes(current)) return false;
    if (blocked.has(current)) return false;
  }

  const destination = path[path.length - 1] as SpaceId;
  return destination === fighter.spaceId || !isOccupied(state, destination, fighter.id);
}

/** Voľné priestory v zónach, kde stojí daná figúrka — na rozostavenie sidekickov. */
export function freeSpacesInZonesOf(
  map: GameMap,
  occupied: ReadonlySet<SpaceId>,
  spaceId: SpaceId,
): SpaceId[] {
  const anchor = map.spaces.find((s) => s.id === spaceId);
  if (!anchor) return [];
  return map.spaces
    .filter((s) => s.id !== spaceId && !occupied.has(s.id) && s.zones.some((z) => anchor.zones.includes(z)))
    .map((s) => s.id);
}
